<script setup lang="ts">
import { computed, ref } from "vue";

import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { colorsByTheme } from "~/constants/style";
import { CheckboxFieldIcon, CheckedIcon, UncheckedIcon } from "~/icons";
import { DropdownMenuItemKind, EditorMode, Placement } from "~/shared/enums";
import type { PropertyCheckbox, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { makeUuid } from "~/utils/common";

type Value = PropertyValueForKind<PropertyCheckbox>;

const props = defineProps<{
  property: PropertyCheckbox;
  tasks: Task[];
  defaultValue?: Value;
  editorMode: EditorMode;
  value?: Value;
}>();

const emit = defineEmits<{
  update: [checked?: Value];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);
const id = ref(`checkbox-${makeUuid()}`);

const isChecked = computed(() => {
  if (props.editorMode === EditorMode.FORM || props.editorMode === EditorMode.AUTOMATION) {
    return props.value ?? false;
  }

  return props.tasks.every((task) => getPropertyValueFromTask(props.property, task));
});

const isChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isFormOrAutomationMode = computed(() => isFormMode.value || props.editorMode === EditorMode.AUTOMATION);

const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);

const updateValue = (value: Value) => {
  if (isFormOrAutomationMode.value) {
    emit("update", value);
    return;
  }

  dataStore.updateTasks(
    selectedTasks.value.map((task) => ({
      duid: task.duid,
      ...getPropertyPartialTask(props.property, task, value),
    }))
  );

  emit("update");
};

const dropdownSections = computed(() => {
  const allChecked = isFormOrAutomationMode.value
    ? props.value
    : selectedTasks.value.every((task) => getPropertyValueFromTask(props.property, task));
  const allUnchecked = isFormOrAutomationMode.value
    ? !props.value
    : selectedTasks.value.every((task) => !getPropertyValueFromTask(props.property, task));

  return [
    {
      title: getPropertyDisplayTitle(props.property),
      items: [
        {
          title: "Checked",
          kind: DropdownMenuItemKind.BUTTON,
          disabled: allChecked,
          icon: CheckedIcon,
          onClick: () => updateValue(true),
        },
        {
          title: "Unchecked",
          kind: DropdownMenuItemKind.BUTTON,
          disabled: allUnchecked,
          icon: UncheckedIcon,
          onClick: () => updateValue(false),
        },
      ],
    },
  ];
});

const onToggle = (e: Event) => {
  const { checked } = e.target as HTMLInputElement;
  // Unlike all of the dropdown-based editors, this one updates immediately, so there is a race condition with grid selection that requires us to wait for it
  // oxlint-disable-next-line dart/no-set-timeout
  setTimeout(() => updateValue(checked));
};
</script>

<template>
  <DropdownMenu
    v-if="isContextMenuMode"
    :sections="dropdownSections"
    mobile-include-back
    :placement="Placement.RIGHT_TOP"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }">
    <DropdownMenuItemContent
      :icon="CheckboxFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
  </DropdownMenu>
  <Tooltip
    v-else
    :disabled="isFormOrAutomationMode"
    :text="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
    :block="isListMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :class="isTaskDetailMode && 'w-full'">
    <div
      class="flex size-full max-w-full items-center rounded-sm text-sm text-md"
      :class="{
        'h-[26px] gap-1 border border-oncolor py-0.5 hover:bg-opposite/10': isChipMode,
        'px-2 hover:bg-lt': isFormOrAutomationMode,
        'min-h-7 justify-start px-2 hover:bg-lt': isTaskDetailMode,
        'px-1': !isFormOrAutomationMode,
        'justify-center': isListMode,
      }">
      <label :for="id" class="sr-only">{{ getPropertyDisplayTitle(property) }}</label>
      <input
        :id="id"
        :class="[isChipMode || isFormOrAutomationMode ? 'icon-sm' : 'icon-xs']"
        class="cursor-pointer rounded-sm border border-hvy bg-transparent text-primary-base focus-ring-none checked:border-primary-base checked:hover-bg-primary hover:bg-md checked:hover:border-primary-hover-light focus:border-hvy dark:checked:border-primary-base dark:checked:hover:border-primary-hover-dark"
        type="checkbox"
        :checked="isChecked"
        @change="onToggle" />
      <span v-if="isChipMode" class="select-none">
        {{ getPropertyDisplayTitle(property) }}
      </span>
    </div>
  </Tooltip>
</template>
